// src/components/Header.tsx
import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  ChevronDown,
  GraduationCap,
  LogOut,
  Settings,
  User,
  Users,
  Star,
  Map,
  Info,
  Mail,
  Shield,
  FileText,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../modules/brand/useBranding";

type HeaderProps = {
  dark: boolean;
  menuOpen: boolean;
  setMenuOpen: (open: boolean) => void;
};

function navLinkClass(isActive: boolean, dark: boolean) {
  const base =
    "px-2 py-1 rounded-full text-sm font-medium transition-colors duration-150";
  if (dark) {
    return (
      base +
      " text-slate-300 hover:text-white hover:bg-slate-800/60" +
      (isActive ? " text-white bg-slate-800" : "")
    );
  }
  return (
    base +
    " text-slate-600 hover:text-slate-900 hover:bg-slate-100" +
    (isActive ? " text-slate-900 bg-slate-200" : "")
  );
}

function getAvatarEmoji(profile: any, fallbackLabel: string) {
  const avatar = profile?.avatar as string | undefined;
  if (avatar === "boy") return "👦";
  if (avatar === "girl") return "👧";
  if (avatar === "man") return "👨";
  if (avatar === "woman") return "👩";
  return (fallbackLabel || "?").charAt(0).toUpperCase() || "👤";
}

export default function Header({ dark, menuOpen, setMenuOpen }: HeaderProps) {
  const navigate = useNavigate();
  const {
    account,
    activeProfile,
    isAuthed,
    isFamily,
    isSchool,
    isPremium,
    interfaceLabel,
    logout,
    isIndividual, // même si pas utilisé partout, on le laisse pour cohérence
    checkAccountPassword,
    selectProfile,
  } = useAuth();
  const brand = useBranding();

  const [moreOpen, setMoreOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [profileSwitcherOpen, setProfileSwitcherOpen] = useState(false);
  const [askPassword, setAskPassword] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [scrolled, setScrolled] = useState(false);

  // refs pour gérer le click-outside
  const moreMenuRef = useRef<HTMLDivElement | null>(null);
  const userMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      if (moreMenuRef.current && !moreMenuRef.current.contains(target)) {
        setMoreOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(target)) {
        setUserOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 4);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const headerClass = dark
    ? "sticky top-0 z-20 border-b border-slate-800 bg-slate-950/80 backdrop-blur"
    : "sticky top-0 z-20 border-b border-slate-200 bg-white/80 backdrop-blur";
  const shadowClass = scrolled
    ? dark
      ? "shadow-[0_12px_40px_rgba(0,0,0,0.35)]"
      : "shadow-[0_12px_40px_rgba(15,23,42,0.12)]"
    : "";

  const shellClass = "max-w-6xl mx-auto px-4 py-3 flex items-center gap-4";

  const brandSubTextClass = dark ? "text-slate-400" : "text-slate-500";

  const pillButtonBase =
    "inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors";

  const pillButtonSecondary = dark
    ? `${pillButtonBase} border-slate-700 text-slate-100 bg-slate-900 hover:bg-slate-800 hover:text-white`
    : `${pillButtonBase} border-slate-200 text-slate-800 bg-white hover:bg-slate-50 hover:text-slate-900`;

  const pillButtonPrimary = dark
    ? `${pillButtonBase} border-blue-500/70 bg-blue-600 text-white hover:bg-blue-500`
    : `${pillButtonBase} border-blue-600 bg-blue-600 text-white hover:bg-blue-700`;

  const premiumPillClass = dark
    ? "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-400/10 text-amber-300 border border-amber-400/40"
    : "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-500/10 text-amber-700 border border-amber-400/70";

  const profileName =
    activeProfile?.firstName || (isAuthed ? "Utilisateur" : "Visiteur");

  const isFamilyOrSchool = isFamily || isSchool;

  const isChildLike = (() => {
    if (!account || !isFamilyOrSchool || !activeProfile) return false;
    const role = (activeProfile as any).role as string | undefined;
    return role === "child" || role === "student";
  })();

  const familyOrSchoolLabel = isSchool ? "Espace school" : "Espace famille";
  const FamilySchoolIcon = isSchool ? GraduationCap : Users;

  const profilesList: any[] =
    account && (account.type === "family" || account.type === "school")
      ? ((account as any).profiles as any[])
      : [];

  function handleLogout() {
    logout();
    setMoreOpen(false);
    setUserOpen(false);
    setProfileSwitcherOpen(false);
    setAskPassword(false);
    setPasswordInput("");
    setPasswordError("");
    navigate("/login");
  }

  function openProfileSwitcher() {
    if (!isFamilyOrSchool) return;
    if (isChildLike) {
      setPasswordInput("");
      setPasswordError("");
      setAskPassword(true);
      setUserOpen(false);
      return;
    }
    setProfileSwitcherOpen(true);
    setUserOpen(false);
  }

  function handlePasswordConfirm() {
    if (!passwordInput.trim()) {
      setPasswordError("Merci de saisir le mot de passe du compte.");
      return;
    }
    const ok = checkAccountPassword
      ? checkAccountPassword(passwordInput)
      : false;
    if (!ok) {
      setPasswordError("Mot de passe incorrect.");
      return;
    }
    setAskPassword(false);
    setPasswordInput("");
    setPasswordError("");
    setProfileSwitcherOpen(true);
  }

  function handleSelectProfile(profileId: string) {
    selectProfile(profileId);
    setProfileSwitcherOpen(false);
  }

  const menuPanelBase =
    "absolute right-0 mt-2 w-56 rounded-2xl border shadow-lg z-30 overflow-hidden";
  const menuPanelTheme = dark
    ? "bg-slate-900/95 border-slate-700 text-slate-100"
    : "bg-white border-slate-200 text-slate-800";
  const menuItemBase = "w-full text-left px-3 py-2 text-sm";
  const menuItemHover = dark ? "hover:bg-slate-800/70" : "hover:bg-slate-100";
  const menuIconClass = dark
    ? "w-4 h-4 text-slate-200"
    : "w-4 h-4 text-slate-600";
  const premiumMenuClass = dark
    ? "bg-amber-500/10 text-amber-100 hover:bg-amber-500/20"
    : "bg-amber-50 text-slate-900 hover:bg-amber-100";

  return (
    <>
      <header className={`mm-header ${headerClass} ${shadowClass}`}>
        <div className={shellClass}>
          {/* Logo / brand */}
          <button
            type="button"
            onClick={() => navigate("/")}
            className="flex items-center gap-2"
          >
            <img
              src={brand.headerLogoSrc}
              alt={brand.headerLogoAlt}
              className="h-9 md:h-10 w-auto"
            />
            <div className="flex flex-col leading-tight text-left">
              {isPremium && (
                <span className={`${premiumPillClass} self-start`}>
                  <Star className="w-3 h-3" />
                  <span>Premium</span>
                </span>
              )}
              <p className={`text-[11px] uppercase tracking-[0.16em] ${brandSubTextClass}`}>
                Plateforme éducative • v0.9 Beta
              </p>
            </div>
          </button>

          {/* Navigation principale (desktop) */}
          <div className="hidden md:flex flex-1 items-center justify-center">
            <div className="flex items-center gap-10">
              <HeaderLinkLarge to="/academy">Academy</HeaderLinkLarge>
              <HeaderLinkLarge to="/play">Play</HeaderLinkLarge>
              <HeaderLinkLarge to="/assist">Assist</HeaderLinkLarge>
            </div>
          </div>

          {/* Actions droite (desktop) */}
          <div className="hidden md:flex items-center gap-3 ml-auto">
            {isFamilyOrSchool && (
              <button
                type="button"
                onClick={() => navigate("/parents")}
                className={pillButtonSecondary}
              >
                <FamilySchoolIcon className="w-4 h-4" />
                <span>{familyOrSchoolLabel}</span>
              </button>
            )}

            {/* Utilisateur / Connexion - click + click-outside */}
            <div className="relative" ref={userMenuRef}>
              {isAuthed ? (
                <button
                  type="button"
                  onClick={() => {
                    setUserOpen((v) => !v);
                    setMoreOpen(false);
                  }}
                  className={pillButtonSecondary}
                >
                  <span className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-900/80 text-xs">
                      {getAvatarEmoji(activeProfile, profileName)}
                    </span>
                    <span className="text-xs font-medium">{profileName}</span>
                    <ChevronDown className="w-3 h-3" />
                  </span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className={pillButtonPrimary}
                >
                  <User className="w-4 h-4" />
                  <span>Connexion</span>
                </button>
              )}

              {isAuthed && userOpen && (
                <div className={`${menuPanelBase} ${menuPanelTheme} animate-menu`}>
                  <div className="px-3 py-2 border-b border-slate-700/40 text-xs">
                    <p className="font-semibold">{profileName}</p>
                    <p className="text-[11px] opacity-70">{interfaceLabel}</p>
                  </div>

                  {isFamilyOrSchool && (
                    <button
                      className={`${menuItemBase} ${menuItemHover} flex items-center gap-2`}
                      onClick={openProfileSwitcher}
                    >
                      <Users className="w-4 h-4" />
                      <span>Changer de profil</span>
                    </button>
                  )}

                  {!isChildLike && (
                    <button
                      className={`${menuItemBase} ${menuItemHover} flex items-center gap-2`}
                      onClick={() => {
                        navigate("/account");
                        setUserOpen(false);
                      }}
                    >
                      <Settings className="w-4 h-4" />
                      <span>Aller au compte</span>
                    </button>
                  )}

                  <button
                    className={`${menuItemBase} flex items-center gap-2 hover:bg-red-500/10 text-red-500`}
                    onClick={handleLogout}
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Se déconnecter</span>
                  </button>
                </div>
              )}
            </div>

            {/* MENU PLUS - click + click-outside */}
            <div className="relative" ref={moreMenuRef}>
              <button
                type="button"
                onClick={() => {
                  setMoreOpen((v) => !v);
                  setUserOpen(false);
                }}
                className={pillButtonSecondary}
              >
                <span>Plus</span>
                <ChevronDown className="w-3 h-3" />
              </button>

              {moreOpen && (
                <div className={`${menuPanelBase} ${menuPanelTheme} animate-menu`}>
                  <button
                    className={`${menuItemBase} ${menuItemHover} flex items-center gap-2 font-semibold ${premiumMenuClass} btn-premium-glow`}
                    onClick={() => {
                      navigate("/premium");
                      setMoreOpen(false);
                    }}
                  >
                    <Star className={menuIconClass} />
                    Découvrir Premium
                  </button>
                  <button
                    className={`${menuItemBase} ${menuItemHover} flex items-center gap-2`}
                    onClick={() => {
                      navigate("/account");
                      setMoreOpen(false);
                    }}
                  >
                    <User className={menuIconClass} />
                    Aller au compte
                  </button>

                  <div className="my-1 border-t border-slate-200 dark:border-slate-700" />

                  <button
                    className={`${menuItemBase} ${menuItemHover} flex items-center gap-2`}
                    onClick={() => {
                      navigate("/roadmap");
                      setMoreOpen(false);
                    }}
                  >
                    <Map className={menuIconClass} />
                    Roadmap
                  </button>
                  <button
                    className={`${menuItemBase} ${menuItemHover} flex items-center gap-2`}
                    onClick={() => {
                      navigate("/about");
                      setMoreOpen(false);
                    }}
                  >
                    <Info className={menuIconClass} />
                    À propos
                  </button>

                  <div className="my-1 border-t border-slate-200 dark:border-slate-700" />

                  <button
                    className={`${menuItemBase} ${menuItemHover} flex items-center gap-2`}
                    onClick={() => {
                      navigate("/contact");
                      setMoreOpen(false);
                    }}
                  >
                    <Mail className={menuIconClass} />
                    Contact
                  </button>

                  <div className="my-1 border-t border-slate-200 dark:border-slate-700" />

                  <button
                    className={`${menuItemBase} ${menuItemHover} flex items-center gap-2`}
                    onClick={() => {
                      navigate("/confidentialite");
                      setMoreOpen(false);
                    }}
                  >
                    <Shield className={menuIconClass} />
                    Confidentialité
                  </button>
                  <button
                    className={`${menuItemBase} ${menuItemHover} flex items-center gap-2`}
                    onClick={() => {
                      navigate("/mentions-legales");
                      setMoreOpen(false);
                    }}
                  >
                    <FileText className={menuIconClass} />
                    Mentions légales
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Bouton menu mobile */}
          <button
            type="button"
            className="md:hidden ml-auto inline-flex items-center justify-center w-9 h-9 rounded-full border border-slate-300 bg-white/80 text-slate-700"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <span className="text-xs font-semibold">
              {menuOpen ? "?" : "="}
            </span>
          </button>
        </div>

        {/* Menu mobile */}
        {menuOpen && (
          <div
            className={`md:hidden border-t ${
              dark
                ? "border-slate-800 bg-slate-950"
                : "border-slate-200 bg-white"
            }`}
          >
            <div className="max-w-6xl mx-auto px-4 py-3 flex flex-col gap-2 text-sm">
              <Link
                to="/academy"
                onClick={() => setMenuOpen(false)}
                className="py-1 text-slate-800 dark:text-slate-100 hover:text-slate-900 dark:hover:text-white"
              >
                Academy
              </Link>
              <Link
                to="/play"
                onClick={() => setMenuOpen(false)}
                className="py-1 text-slate-800 dark:text-slate-100 hover:text-slate-900 dark:hover:text-white"
              >
                Play
              </Link>
              <Link
                to="/assist"
                onClick={() => setMenuOpen(false)}
                className="py-1 text-slate-800 dark:text-slate-100 hover:text-slate-900 dark:hover:text-white"
              >
                Assist
              </Link>

              {isFamilyOrSchool && (
                <Link
                  to="/parents"
                  onClick={() => setMenuOpen(false)}
                  className="py-1 text-slate-800 dark:text-slate-100 hover:text-slate-900 dark:hover:text-white"
                >
                  {familyOrSchoolLabel}
                </Link>
              )}

              <Link
                to="/premium"
                onClick={() => setMenuOpen(false)}
                className="py-1 text-slate-800 dark:text-slate-100 hover:text-slate-900 dark:hover:text-white"
              >
                Découvrir Premium
              </Link>
              <Link
                to="/about"
                onClick={() => setMenuOpen(false)}
                className="py-1 text-slate-800 dark:text-slate-100 hover:text-slate-900 dark:hover:text-white"
              >
                À propos
              </Link>
              <Link
                to="/contact"
                onClick={() => setMenuOpen(false)}
                className="py-1 text-slate-800 dark:text-slate-100 hover:text-slate-900 dark:hover:text-white"
              >
                Contact
              </Link>
              <Link
                to="/roadmap"
                onClick={() => setMenuOpen(false)}
                className="py-1 text-slate-800 dark:text-slate-100 hover:text-slate-900 dark:hover:text-white"
              >
                Roadmap
              </Link>
              <Link
                to="/confidentialite"
                onClick={() => setMenuOpen(false)}
                className="py-1 text-slate-800 dark:text-slate-100 hover:text-slate-900 dark:hover:text-white"
              >
                Confidentialité
              </Link>
              <Link
                to="/mentions-legales"
                onClick={() => setMenuOpen(false)}
                className="py-1 text-slate-800 dark:text-slate-100 hover:text-slate-900 dark:hover:text-white"
              >
                Mentions légales
              </Link>

              {isAuthed && (
                <>
                  <div className="mt-2 border-t border-slate-200 pt-2 text-xs">
                    <p className="font-semibold">{profileName}</p>
                    <p className="text-[11px] opacity-70">{interfaceLabel}</p>
                  </div>

                  {isFamilyOrSchool && (
                    <button
                      className="text-left py-1"
                      onClick={() => {
                        setMenuOpen(false);
                        openProfileSwitcher();
                      }}
                    >
                      Changer de profil
                    </button>
                  )}

                  {!isChildLike && (
                    <button
                      className="text-left py-1"
                      onClick={() => {
                        setMenuOpen(false);
                        navigate("/account");
                      }}
                    >
                      Aller au compte
                    </button>
                  )}

                  <button
                    className="text-left py-1 text-red-500"
                    onClick={() => {
                      setMenuOpen(false);
                      handleLogout();
                    }}
                  >
                    Se déconnecter
                  </button>
                </>
              )}

              {!isAuthed && (
                <button
                  className="mt-2 inline-flex items-center gap-2 self-start text-xs font-medium px-3 py-1.5 rounded-full border border-blue-500 text-blue-600"
                  onClick={() => {
                    setMenuOpen(false);
                    navigate("/login");
                  }}
                >
                  <User className="w-4 h-4" />
                  <span>Connexion</span>
                </button>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Modal mot de passe pour changement de profil depuis un enfant/élève */}
      {askPassword && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 px-4">
          <div
            className={`w-full max-w-md rounded-3xl border p-5 space-y-4 animate-modal ${
              dark
                ? "bg-slate-950 border-slate-700 text-slate-100"
                : "bg-white border-slate-200 text-slate-800"
            }`}
          >
            <div className="space-y-1">
              <p className="text-sm font-semibold">
                Confirmer avec le mot de passe du compte
              </p>
              <p className="text-xs opacity-70">
                Pour changer de profil, un parent ou un enseignant doit saisir
                le mot de passe du compte.
              </p>
            </div>
            <div className="space-y-2 text-sm">
              <input
                type="password"
                value={passwordInput}
                onChange={(e) => {
                  setPasswordInput(e.target.value);
                  setPasswordError("");
                }}
                className={`w-full rounded-xl border px-3 py-2 text-sm outline-none ${
                  dark
                    ? "bg-slate-900 border-slate-700 text-slate-50"
                    : "bg-white border-slate-300 text-slate-900"
                }`}
                placeholder="Mot de passe du compte"
              />
              {passwordError && (
                <p className="text-xs text-red-500">{passwordError}</p>
              )}
            </div>
            <div className="flex justify-end gap-2 text-xs">
              <button
                className="px-3 py-1.5 rounded-full border border-slate-500/60"
                onClick={() => {
                  setAskPassword(false);
                  setPasswordInput("");
                  setPasswordError("");
                }}
              >
                Annuler
              </button>
              <button
                className="px-3 py-1.5 rounded-full bg-blue-600 text-white"
                onClick={handlePasswordConfirm}
              >
                Continuer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sélecteur de profil (drawer centré) */}
      {profileSwitcherOpen && (
        <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/60 px-4">
          <div
            className={`w-full max-w-xl rounded-3xl border p-5 space-y-4 animate-modal ${
              dark
                ? "bg-slate-950 border-slate-700 text-slate-100"
                : "bg-white border-slate-200 text-slate-800"
            }`}
          >
            <div className="flex items-center justify-between gap-3 mb-1">
              <div>
                <p className="text-sm font-semibold">
                  Choisir un profil {isSchool ? "school" : "famille"}
                </p>
                <p className="text-xs opacity-70">
                  Sélectionne le profil à utiliser pour continuer à explorer
                  MixMind.
                </p>
              </div>
              <button
                className="text-xs px-3 py-1.5 rounded-full border border-slate-500/60"
                onClick={() => setProfileSwitcherOpen(false)}
              >
                Fermer
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              {profilesList.map((p) => {
                const pName = p.firstName || "Profil";
                const role = (p.role as string) || "";
                const roleLabel =
                  role === "parent"
                    ? "Parent"
                    : role === "child"
                    ? "Enfant"
                    : role === "teacher"
                    ? "Enseignant"
                    : role === "student"
                    ? "Élève"
                    : "";

                const isActive =
                  activeProfile && (activeProfile as any).id === p.id;

                return (
                  <button
                    key={p.id}
                    type="button"
                    className={`flex items-center gap-3 rounded-2xl border px-3 py-2 text-left transition ${
                      isActive
                        ? dark
                          ? "border-blue-400 bg-blue-500/10"
                          : "border-blue-500 bg-blue-50"
                        : dark
                        ? "border-slate-700 hover:border-blue-500 hover:bg-slate-900"
                        : "border-slate-200 hover:border-blue-500 hover:bg-slate-50"
                    }`}
                    onClick={() => handleSelectProfile(p.id)}
                  >
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-2xl bg-slate-900/80 text-sm">
                      {getAvatarEmoji(p, pName)}
                    </span>
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">{pName}</span>
                      <span className="text-[11px] opacity-70">
                        {roleLabel}
                        {isActive && " · profil actif"}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
function HeaderLinkLarge({
  to,
  children,
}: {
  to: string;
  children: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        [
          "text-base md:text-lg font-semibold tracking-tight px-3 py-1.5 rounded-full border transition-colors duration-200",
          "text-slate-900/90 hover:text-slate-950 hover:bg-sky-50 hover:border-sky-200",
          "dark:text-slate-100/85 dark:hover:text-slate-50 dark:hover:bg-slate-900/60 dark:hover:border-slate-700",
          isActive
            ? "bg-sky-100 text-slate-950 border-sky-400 shadow-sm dark:bg-slate-900 dark:text-white dark:border-blue-500/60"
            : "border-transparent text-slate-800 dark:text-slate-300",
        ].join(" ")
      }
    >
      {children}
    </NavLink>
  );
}






